import { useRef, useState } from "react";
import { Upload, X, ChevronLeft, ChevronRight } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";

export function MultiImageUpload({ value = [], onChange, label = "Images", testid = "multi-image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const images = value || [];

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      }
      onChange([...images, ...uploaded]);
      toast.success(uploaded.length > 1 ? `${uploaded.length} images uploaded` : "Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= images.length) return;
    const arr = [...images];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };

  return (
    <div data-testid={testid}>
      <label className="block text-sm font-medium text-[#333333] mb-1">{label}</label>
      <p className="text-xs text-[#555555] mb-2">Add multiple images — they rotate automatically on the public site. Use the arrows to set the order.</p>
      <div className="flex flex-wrap items-start gap-3">
        {images.map((url, i) => (
          <div key={`${url}-${i}`} className="relative group" data-testid={`${testid}-item-${i}`}>
            <img src={fileUrl(url)} alt={`Image ${i + 1}`} className="h-20 w-28 object-cover rounded-md border border-[#E5E7EB]" />
            <button type="button" onClick={() => remove(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5" data-testid={`${testid}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
            <div className="absolute bottom-1 left-1 right-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="bg-black/60 text-white rounded p-0.5 disabled:opacity-30" data-testid={`${testid}-left-${i}`}><ChevronLeft className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} className="bg-black/60 text-white rounded p-0.5 disabled:opacity-30" data-testid={`${testid}-right-${i}`}><ChevronRight className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testid}-btn`}
          className="h-20 w-28 inline-flex flex-col items-center justify-center gap-1 text-sm border border-dashed border-[#1E63C4] text-[#1E63C4] rounded-md hover:bg-blue-50 transition-colors disabled:opacity-60"
        >
          <Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Add"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" />
      </div>
    </div>
  );
}
